<template>
  <div class="qb-category-tags-panel">
    <div class="qb-category-panel">
      <h3 class="title text-lg text-orange">{{ $t('category_tags.categories') }}</h3>
      <draggable-list
        containerClass="list orange"
        type="category"
        :data="categoriesData"
        :selected="selectedCategory"
        :searchable="true"
        :isListLoading="categories.isBusy"
        :isInsight="isInsight"
        :onSearch="onSearchCategories"
        :onSelect="onSelectCategory"
        :onDrag="(e, value) => onDragCategory(e, value)"
        :onDragEnd="onDragEndTags"
        @toggleExpandedOption="onExpandCategory"
      />
    </div>

    <div class="qb-tag-panel">
      <h3 class="title text-lg text-marine">{{ $t('category_tags.tags') }}</h3>
      <draggable-list
        containerClass="list marine"
        :data="tagsData"
        type="category"
        :selected="selectedTags"
        :searchable="true"
        :isListLoading="category.isBusy"
        :pagination="categoryTagsPagination"
        :isInitiated="isInitiated"
        :onSearch="onSearchTags"
        :onSelect="onSelectTag"
        :onDrag="(e, value) => onDragTags(e, value)"
        :onDragEnd="onDragEndTags"
        @onInfiniteLoad="tagsInfiniteLoad"
      />
    </div>
  </div>
</template>

<script>
  import { mapState, mapActions } from 'vuex';
  import _ from 'lodash';

  import DraggableList from './draggable_list';

  import { QUERIES } from '../../config/const';

  export default {
    components: {
      DraggableList
    },
    props: {
      query: {
        type: Object,
        default: () => {}
      },
      isInsight:{
        type: Boolean,
        default: false
      },
      onDrag: Function
    },
    data: function() {
      return {
        selectedCategory: null,
        selectedTags: []
      }
    },
    computed: {
      ...mapState({
        categories: state => state.queryBuilder.categories.categories.data || {},
        category: state => state.queryBuilder.category.category,
        categoryTagsPagination: state => state.queryBuilder?.category?.category?.pagination,
        isInitiated: state => state.queryBuilder.category?.category?.isInitiated
      }),
      hasHiddenForInsight (){
        return this.categories && this.categories.has_hidden_for_insight || false
      },
      projectId () {
        return this.$route.params.project_id;
      },
      categoriesData () {
        let data = [];
        if (this.categories.data) {
          data = this.categories.data.map(category => {
            const options = [];
            let expandable = false, isActive = false;

            const inAxisRows = (this.query.rows || []).filter(dt => (dt.type === 'category' && dt.id === category.id) || (dt.type === 'tag' && dt.category_id === category.id)).length > 0;
            const inAxisCols = (this.query.columns || []).filter(dt => (dt.type === 'category' && dt.id === category.id) || (dt.type === 'tag' && dt.category_id === category.id)).length > 0;
            
            const itemCss = [];
            if (inAxisRows) itemCss.push('in-axis-rows');
            if (inAxisCols) itemCss.push('in-axis-cols');

            if (category.is_sub_organizations_category && Array.isArray(category.sub_options) && category.sub_options.length > 0) {
              expandable = true;
              isActive = (this.selectedCategory?.id === category.id || options.filter(dt => dt.id === this.selectedCategory?.id).length > 0)
              options.push(...category.sub_options.map(dt => ({
                ...dt,
                category_type_id: category.category_type_id,
                category_id: category.id,
                sub_category_id: dt.id,
                draggable_to: [QUERIES.ALLOW_DRAG.AXIS, QUERIES.ALLOW_DRAG.EXTERNAL_FILTERS],
                itemCss: itemCss.join(' '),
                inAxis: inAxisRows || inAxisCols
              })));
            }

            return {
              id: category.id,
              name: category.name,
              category_type_id: category.category_type_id,
              hasChildren: category.has_children,
              hidden_on_insight: category.hidden_on_insight,
              draggable_to: [QUERIES.ALLOW_DRAG.AXIS, QUERIES.ALLOW_DRAG.EXTERNAL_FILTERS],
              expandable: expandable,
              sub_options: options,
              is_active: expandable && (this.selectedCategory?.id === category.id || options.filter(dt => dt.id === this.selectedCategory?.id).length > 0),
              itemCss: itemCss.join(' '),
              inAxis: inAxisRows || inAxisCols
            }
          })
        }

        return data.sort((a, b) => a.inAxis === b.inAxis ? 0 : (a.inAxis ? -1 : 1));
      },
      tagsData () {
        let data = [];
        if (this.category.data && !_.isEmpty(this.category.data.tags)) {
          const categoryId = this.category.data.tags[0].category_id;
          const categoryTypeId = this.category.data.tags[0].category_type_id;
          data = this.category.data.tags.map(tag => {
            return {
              id: tag.id,
              name: tag.name,
              draggable_to: [QUERIES.ALLOW_DRAG.AXIS, QUERIES.ALLOW_DRAG.EXTERNAL_FILTERS, QUERIES.ALLOW_DRAG.FILTERS],
              category_id: categoryId,
              category_type_id: categoryTypeId
            }
          });

          if (!this.selectedCategory?.sub_category_id) {
            data.unshift({
              id: 'all',
              type: this.$t('category_tags.any_tag'),
              name: `${this.$t('category_tags.any_tag')} - ${this.category.data.name}`,
              value: 'all', 
              draggable_to: [QUERIES.ALLOW_DRAG.AXIS, QUERIES.ALLOW_DRAG.FILTERS],
              category_id: categoryId,
              category_type_id: categoryTypeId
            });
          }
        }

        return data.map(dt => {
          const inAxisRows = (this.query.rows || []).find(t => t.type === 'tag' && t.tag_id === dt.id && t.category_id === dt.category_id);
          const inAxisCols = (this.query.columns || []).find(t => t.type === 'tag' && t.tag_id === dt.id && t.category_id === dt.category_id);

          const itemCss = [];
          if (inAxisRows) itemCss.push('in-axis-rows');
          if (inAxisCols) itemCss.push('in-axis-cols');

          return { ...dt, itemCss: itemCss.join(' ') }
        });
      }
    },
    methods: {
      ...mapActions({
        'getCategories': 'queryBuilder/categories/getCategories',
        'getCategory': 'queryBuilder/category/getCategory',
      }),
      loadCategories (filterValues) {
        const { projectId } = this;
        this.getCategories({
          projectId,
          filterValues
        });
      },
      loadCategory(categoryId, filterValues, pagination) {
        const { projectId } = this;
        this.getCategory({
          projectId,
          categoryId,
          filterValues,
          pagination
        });
      },
      tagsInfiniteLoad() {
        let nextPage = typeof this.categoryTagsPagination === 'object' && this.categoryTagsPagination?.currentPage + 1;
        const loadCategory = nextPage > this.categoryTagsPagination?.currentPage && nextPage <= this.categoryTagsPagination?.totalPages;
        if(loadCategory) {
          this.loadCategory(this.selectedCategory.id, { query: this.searchValue, sub_category_id: this.selectedCategory.sub_category_id }, { ...this.categoryTagsPagination, currentPage: nextPage });
        }        
      },
      onSearchCategories (value) {
        let filters = {isInsight: this.isInsight};
        if (value != '') {
          filters.query = value;
        }
        this.loadCategories(filters);
      },
      onSelectCategory ({ option }) {
        this.selectedCategory = option;
        this.loadCategory(option.category_id || option.id, { sub_category_id: option.sub_category_id });
      },
      onSearchTags (value) {
        if(this.searchValue === value?.trim()) return;
        const categoryId = this.selectedCategory.category_id || this.selectedCategory.id;
        this.searchValue = value?.trim();
        this.loadCategory(categoryId, {
          query: value,
          sub_category_id: this.selectedCategory.sub_category_id
        }, { ...this.categoryTagsPagination, currentPage: 1 });
      },
      onSelectTag ({ option, event }) {
        let tags = this.selectedTags;
        const selectedIndex = tags.findIndex(opt => opt.id === option.id);
        const alreadySelected = selectedIndex === -1 ? false : true;

        if (
          alreadySelected &&
          !event.shiftKey &&
          !event.ctrlKey && !event.metaKey
        ) {
          tags = tags.splice(selectedIndex, 1);
        } else if (
          alreadySelected &&
          !event.shiftKey &&
          (event.ctrlKey || event.metaKey)
        ) {
          tags.splice(selectedIndex, 1);
        } else if (
          !alreadySelected ||
          (
            alreadySelected &&
            event.shiftKey  &&
            !event.ctrlKey &&
            !event.metaKey
          )
        ) {
          tags.push(option);
        }

        this.selectedTags = tags;
      },
      onDragCategory (e, category) {
        if(category && category.has_children === false) {
          return;
        }

        this.onSelectCategory({ option: category });

        const dragItem = [{
            type:'category',
            name: category.name,
            id: category.category_id || category.id,
            category_type_id: category.category_type_id,
            sub_category_id: category.sub_category_id
        }];

        e.dataTransfer.setData('filters', JSON.stringify(dragItem));
        this.onDrag(category.draggable_to, dragItem);
      },
      onDragTags (e, value) {
        const { selectedCategory } = this;
        const data = value.map(tag => {
          return {
            type: 'tag',
            tag_id: tag.id,
            tag_name: tag.name,
            category_id: tag.category_id || selectedCategory.id,
            category_name: selectedCategory.name,
            category_type_id: tag.category_type_id || selectedCategory.category_type_id,
            draggable_to: tag.draggable_to
          }
        });

        e.dataTransfer.setData('filters', JSON.stringify(data));

        let allowDrag = data[0].draggable_to;
        if(data.find(t => t.tag_id === 0)) {
          allowDrag = [QUERIES.ALLOW_DRAG.AXIS];
        }
        else if (Object.keys(data).length > 1 && allowDrag.indexOf(QUERIES.ALLOW_DRAG.EXTERNAL_FILTERS) !== -1) {
          allowDrag = allowDrag.filter(drag => drag !== QUERIES.ALLOW_DRAG.EXTERNAL_FILTERS);
        }

        this.onDrag(allowDrag, data);
      },
      onDragEndTags () {
        this.selectedTags = [];
        this.onDrag(false);
      },
      onExpandCategory (option) {
        if (this.selectedCategory?.id === option.id) {
          this.selectedCategory = null;
        } else {
          this.selectedCategory = option;
        }
      }
    },
    mounted () {
      this.loadCategories({ isInsight: this.isInsight });
    },
    updated (){
      if(this.hasHiddenForInsight){
        this.$emit('setCategoriesHiddenForInsight', true);
      }

    },  
    watch: {
      'categories.data': {
        handler(value) {
          if (value.length > 0) {
            this.onSelectCategory({ option: value[0] });
          }
        }
      },
    }
  }
</script>

<style lang="scss">
  @import "../../styles/components/coding-queries/category_tags_panel";
</style>